import { useEffect, useState } from "react";

/**
 * useMediaQuery — subscribe to a `window.matchMedia(query)` result.
 *
 * Returns `false` on the server (SSR-safe) and switches to the live value
 * after hydration. Listener uses the modern `addEventListener("change")`
 * API; falls back to `addListener` for legacy Safari < 14.
 *
 * @example
 *   const isDesktop = useMediaQuery("(min-width: 1024px)");
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);

    // Sync once on mount in case the query changed between SSR + hydration.
    setMatches(mql.matches);

    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }

    // Safari < 14 fallback
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}
